// src/resources/users.js
import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    List,
    Datagrid,
    TextField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    required,
    email,
    SelectInput,
    PasswordInput,
    useDataProvider,
    useNotify,
    useRedirect,
    usePermissions,
    useInput
} from 'react-admin';
import {
    Box,
    IconButton,
    InputAdornment,
    Tooltip,
    Typography,
    FormHelperText
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Custom component for the team selection dropdown
const TeamSelectInput = () => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                // Use the standard getList method instead of a custom getTeams method
                const { data } = await dataProvider.getList('teams', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: {}
                });

                setTeams(data.map(team => ({ id: team.id, name: team.name })));
            } catch (error) {
                console.error('Error loading teams:', error);
                notify('Error loading teams', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchTeams();
    }, [dataProvider, notify]);

    return (
        <SelectInput 
            source="team_id" 
            choices={teams} 
            optionText="name" 
            optionValue="id" 
            validate={required()}
            fullWidth
            disabled={loading}
            helperText={loading ? 'Loading teams...' : 'Select a team for this user'}
        />
    );
};

// Custom password input with generate password button
const PasswordInputWithGenerator = ({ source, label, validate, isRequired = false }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const notify = useNotify();
    
    // Use useInput to properly interact with react-admin's form
    const {
        field
    } = useInput({
        source,
        validate
    });

    // Function to generate a random password
    const generateRandomPassword = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+";
        let password = '';
        
        // Generate a 20-character password
        for (let i = 0; i < 20; i++) {
            const randomIndex = Math.floor(Math.random() * chars.length);
            password += chars[randomIndex];
        }
        
        // Update both our local state and the form field
        setGeneratedPassword(password);
        field.onChange(password);
        
        // Show the password
        setShowPassword(true);
        
        // Show success message
        notify('Random password generated', { type: 'info' });
    };

    // Function to copy password to clipboard
    const copyToClipboard = () => {
        navigator.clipboard.writeText(field.value || generatedPassword);
        notify('Password copied to clipboard', { type: 'success' });
    };

    // Update generatedPassword when field value changes externally
    useEffect(() => {
        if (field.value && field.value !== generatedPassword) {
            setGeneratedPassword(field.value);
        }
    }, [field.value, generatedPassword]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <PasswordInput
                    source={source}
                    label={label}
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box sx={{ display: 'flex' }}>
                                    <Tooltip title="Generate random password">
                                        <IconButton
                                            onClick={generateRandomPassword}
                                            color="primary"
                                        >
                                            <AutorenewIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {generatedPassword && (
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                        Generated password:
                    </Typography>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 1, 
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            position: 'relative',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <Box sx={{ 
                            flexGrow: 1, 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {generatedPassword}
                        </Box>
                        <Tooltip title="Copy to clipboard">
                            <IconButton
                                size="small"
                                onClick={copyToClipboard}
                                sx={{ ml: 1 }}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <FormHelperText>
                        Remember to copy this password to a secure location. It won't be visible after saving.
                    </FormHelperText>
                </Box>
            )}
        </Box>
    );
};



// Role selection options
const roleChoices = [
    { id: 'team_admin', name: 'Team Admin' },
    { id: 'tenant_admin', name: 'Tenant Admin' }
];

const UserTitle = ({ record }) => {
    return <span>User: {record ? record.username : ''}</span>;
};

const UserList = () => {
    const { permissions } = usePermissions();

    // Only tenant_admin should see this page
    if (permissions !== 'tenant_admin') {
        return <div>You don't have permission to view users.</div>;
    }
    
    return (
        <List 
            bulkActionButtons={false}
            sort={{ field: 'username', order: 'ASC' }}
        >
            <Datagrid 
                rowClick="edit" 
                bulkActionButtons={false} 
                sx={{ 
                    '& [data-field="__selection__"]': { display: 'none' },
                    '& .MuiTableCell-paddingCheckbox': { display: 'none' },
                    // Making column headers bold
                    '& .MuiTableCell-head': {
                        fontWeight: 'bold',
                    }
                }}
            >
                <TextField source="username" sortable={true} />
                <TextField source="role" sortable={true} />
            </Datagrid>
        </List>
    );
};

const UserEdit = () => {
    const redirect = useRedirect();
    const { permissions } = usePermissions();

    // Check permissions on component mount
    useEffect(() => {
        const fetchData = async () => {
            // If not tenant_admin, redirect
            if (permissions !== 'tenant_admin') {
                redirect('/dashboard');
            }
        };

        fetchData();
    }, [permissions, redirect]);

    // The redirect is now handled in the useEffect, but we still return null
    // for non-admin users to prevent component rendering before redirect
    if (permissions !== 'tenant_admin') {
        return null;
    }

    return (
        <Edit
            title={<UserTitle />}
            mutationMode="pessimistic"
            redirect="list"
        >
            <SimpleForm>
                <TextInput 
                    source="username" 
                    validate={[required(), email()]} 
                    fullWidth 
                    helperText="Username must be a valid email address"
                />
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mt: 2, 
                        mb: 0.5, 
                        color: 'text.secondary',
                        fontWeight: 'medium'
                    }}
                >
                    Password
                </Typography>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        mb: 2, 
                        color: 'text.secondary' 
                    }}
                >
                    Leave empty to keep current password
                </Typography>
                <PasswordInputWithGenerator 
                    source="password" 
                    label="New Password"
                />
                <SelectInput 
                    source="role" 
                    choices={roleChoices} 
                    validate={[required()]} 
                    fullWidth 
                />
                <TeamSelectInput />
            </SimpleForm>
        </Edit>
    );
};

const UserCreate = () => {
    const redirect = useRedirect();
    const { permissions } = usePermissions();

    // Check permissions on component mount
    useEffect(() => {
        const fetchData = async () => {
            // If not tenant_admin, redirect
            if (permissions !== 'tenant_admin') {
                redirect('/dashboard');
            }
        };

        fetchData();
    }, [permissions, redirect]);

    // The redirect is now handled in the useEffect, but we still return null
    // for non-admin users to prevent component rendering before redirect
    if (permissions !== 'tenant_admin') {
        return null;
    }

    return (
        <Create
            redirect="list"
        >
            <SimpleForm>
                <TextInput 
                    source="username" 
                    validate={[required(), email()]} 
                    fullWidth 
                    helperText="Username must be a valid email address"
                />
                <PasswordInputWithGenerator 
                    source="password" 
                    label="Password"
                    validate={[required()]}
                    isRequired={true}
                />
                <SelectInput 
                    source="role" 
                    choices={roleChoices} 
                    validate={[required()]} 
                    defaultValue="team_admin"
                    fullWidth 
                />
                <TeamSelectInput />
            </SimpleForm>
        </Create>
    );
};

export { UserList, UserEdit, UserCreate };